import { type SDK_ViewStackQuery, type SsrData, clientConfig, getStackFromUrl, strings, updateDeprecatedTokens } from "@quickpoint.me/shared";
import { type AtomEffect, atom, atomFamily, selector } from "recoil";
import { IsEmbedded, IsInAuthor, IsInPresentationMode } from "./ui";

export const InjectedStackCfKeyAtom = atom<string | null>({
  key: "VS_InjectedStackCfKey",
  default: null,
});

declare const window: {
  __SSR_DATA: SsrData | undefined;
} & Window;

// if the viewer is embedded in the Author these don't return anything
// as all the data will be injected by the Author

export const StackCfKey = selector<string>({
  key: "VS_StackCfKey",
  get: ({ get }) => {
    const injectedKey = get(InjectedStackCfKeyAtom);
    if (injectedKey) return injectedKey;

    const isInAuthor = get(IsInAuthor);
    const isEmbedded = get(IsEmbedded);
    const isInPresentationMode = get(IsInPresentationMode);



    if (isInAuthor && !isEmbedded) return strings.utility.inAuthor;
    if (isInPresentationMode) return strings.utility.inAuthor;

    const stack = getStackFromUrl(window.location.pathname);
    const hostname = window.location.hostname.toLowerCase();
    const isM23 = hostname === 'mrkt23.com' || hostname === 'www.mrkt23.com';
    const isTkn = hostname === 'tkn.exchange' || hostname === 'www.tkn.exchange';
    //console.log(hostname)
    // //console.log("isM23:", isM23)
    // //console.log("isTKN:", isTkn)

    // fireCampaignEventFromUrl(window.location.href);


    if (!stack.ownerName) {
      if (isTkn) return strings.common.landing.tkn
      if (isM23) return strings.common.landing.m23;
      return strings.common.landing.qp
    }
    // if only the owner name use that and the server will return all published stories for that owner
    if (stack.ownerName && !stack.stackCfKey) {
      return stack.ownerName
    }
    return stack.stackCfKey;
  },
});

const rawCodeEffect = (stackCfKey: string): AtomEffect<SDK_ViewStackQuery["viewStack"]> =>
  ({ setSelf, trigger }) => {
    // Initialized by an explicit set (tests, injected data): the value is
    // already supplied — fetching would race it and leak a dangling promise.
    if (trigger === "set") return;
    if (stackCfKey === strings.utility.inAuthor) {
      setSelf({ code: "", stackId: "", audioFiles: [], filters: [], images: [], dataFiles: [] });
      return;
    }

    const ssrData = window.__SSR_DATA;
    if (ssrData) {
      const rawCode: SDK_ViewStackQuery["viewStack"] = {
        code: ssrData.code ? updateDeprecatedTokens(ssrData.code) : ssrData.code,
        filters: ssrData.filters,
        stackId: ssrData.stackId,
        images: ssrData.images,
        audioFiles: ssrData.audioFiles,
        dataFiles: ssrData.dataFiles,
        hideAttribution: ssrData.hideAttribution
      };


      setSelf(rawCode);
      return
    }

    // Lazy load GraphQL libraries only when SSR data is not available
    Promise.all([
      import("graphql-request"),
      import("@quickpoint.me/shared").then(m => m.getSdk)
    ]).then(([{ GraphQLClient }, getSdk]) => {
      const client = new GraphQLClient(clientConfig.gqlViewStack());
      const sdk = getSdk(client);

      sdk.ViewStack({
        stackCfKey,
      }).then((res) => {
        if (!res.viewStack.images) res.viewStack.images = [];
        if (!res.viewStack.code) {
          setSelf({ code: "There is no stack at this address.", stackId: "", audioFiles: [], filters: [], images: [], dataFiles: [] });
        } else {
          res.viewStack.code = res.viewStack.code ? updateDeprecatedTokens(res.viewStack.code) : res.viewStack.code;
          setSelf(res.viewStack);
        }
      }).catch((e) => {
        console.error(e);
        setSelf({ code: strings.messages.error, stackId: "", audioFiles: [], filters: [], images: [], dataFiles: [] });
      });
    });
  };

export const RawCode = atomFamily<SDK_ViewStackQuery["viewStack"], string>({
  key: "VS_RawCode",
  effects: (stackCfKey) => [rawCodeEffect(stackCfKey)],
});


export const StoryBackGroundAtom = atom<{ content: string[], tapped: boolean, atomId: number } | null>({
  key: "VS_StoryBackGroundContent",
  default: null,
});